<template>
    <div class="head">
        <div class="title">{{ title }}</div>
        <GoHomeButton v-if="isHomeButtonVisible" @click.prevent="onGoHomeButtonClick" />
        <GoBackButton v-if="isGoBackButtonVisible" @click.prevent="onGoBackButtonClick" />
        <UserMenuComponent />
    </div>

    <OperatorOfferOverview
        v-if="showOperatorOfferOverview"
        @addToCartPopoverClick="onArticleClick"
        @quickAddClick="quickAddItem"
    />

    <OfferOverview
        v-if="!showOperatorOfferOverview"
        ref="offerOverviewComponent"
        @articleClick="onArticleClick"
        @scrolledChanged="onScrolledChanged"
    />

    <div class="bottom">
        <div class="selectionsum">{{ selectedArticlesText }}</div>
        <PayButtonComponent
            :action="trans.general.continue"
            :amount="totalAmount"
            :disabled="!isPayButtonEnabled"
            @click="onProceedButtonClick"
        />
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { OrderController } from "@/app/controller/OrderController";
    import { trans } from "@/app/localization/Trans";
    import { AddToCardPopover } from "@/app/popover/AddToCardPopover";
    import { Helper } from "@/app/ui/Helper";
    import { Popup } from "@/components/dialog/Popup";
    import { getPreselectedOptions } from "@/components/order/preselectedArticleOptions";
    import { useAddToCart } from "@/components/order/useAddToCart";
    import type { ICustomerArticle, ICustomerArticleWithOptions } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import PayButtonComponent from "@/app/components/footer/PayButtonComponent.vue";
    import GoBackButton from "@/app/components/header/GoBackButton.vue";
    import GoHomeButton from "@/app/components/header/GoHomeButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import OfferOverview from "@/app/components/order/OfferOverview.vue";
    import OperatorOfferOverview from "@/app/components/order/OperatorOfferOverview.vue";

    const identity = useIdentityStore();
    const offer = useOfferStore();
    const cart = useCartStore();

    // header
    const title = ref<string>();
    const isHomeButtonVisible = ref(true);
    const isGoBackButtonVisible = ref(false);

    const isViewVisible = ref(false);
    const isMounted = ref(false);
    const showOperatorOfferOverview = ref(false);

    OrderController.onShown.add(onBecomeVisible);
    OrderController.onHidden.add(onBecomeInvisible);
    OrderController.onReset.add(reset);

    // html refs
    const offerOverviewComponent = ref<InstanceType<typeof OfferOverview> | null>(null);

    // there are some cases where the view is shown before the component is mounted
    // in this case we need to save the data and use it when the component is mounted
    let deferredBecomeVisibleData: { isMainNavigation: boolean; hasPreorderTimeStep?: boolean } | undefined;

    // init components
    onMounted(() => {
        constructor();
        isMounted.value = true;

        if (deferredBecomeVisibleData) {
            onBecomeVisible(deferredBecomeVisibleData);
            deferredBecomeVisibleData = undefined;
        }
    });

    onBeforeUnmount(() => {
        TastierClient.context.onOfferChanged.remove(redrawList);
        TastierClient.context.onCartChanged.remove(updateCart);
        isMounted.value = false;
    });

    let addToCardPopover: AddToCardPopover | undefined;
    let needsOfferReload = true;

    const selectedArticles = ref(0);
    const selectedArticlesText = computed(() => trans.order.itemsSelected.replace("{0}", selectedArticles.value + ""));

    const totalAmount = ref<string>(toMoneyWithSign(0));
    const isPayButtonEnabled = ref(false);

    function constructor() {
        updateTitle();

        // init bottom
        totalAmount.value = toMoneyWithSign(0);
        isPayButtonEnabled.value = false;

        TastierClient.context.onOfferChanged.add(redrawList);
    }

    async function onBecomeVisible(data?: { isMainNavigation: boolean; hasPreorderTimeStep?: boolean }) {
        if (!isMounted.value) {
            deferredBecomeVisibleData = data;
            return;
        }

        isViewVisible.value = true;
        TastierClient.context.onCartChanged.add(updateCart);

        const currentUseCase = Helper.getCurrentOrderServiceUseCase();
        const previousShowOperatorOfferOverview = showOperatorOfferOverview.value;
        showOperatorOfferOverview.value =
            identity.hasOperatorAccess && currentUseCase?.useOperatorOfferOverview == true;
        await nextTick();

        if (previousShowOperatorOfferOverview !== showOperatorOfferOverview.value) {
            offerOverviewComponent.value?.redrawList();
        }

        // update header
        updateTitle();
        updateHeaderButtonsVisibility(data);

        //replace this with less overhead
        if (needsOfferReload) {
            offerOverviewComponent.value?.setIsLoading();
            TastierClient.context.reloadOffer();
            needsOfferReload = false;
        } else {
            updateCart();
        }

        TastierClient.context.loadFulfillmentsIfNeeded();
        setTimeout(() => {
            displayConfirmationPopover();
        }, 500);
    }

    async function updateHeaderButtonsVisibility(
        data: { isMainNavigation: boolean; hasPreorderTimeStep?: boolean | undefined } | undefined
    ) {
        // if no info about service or is coming back from another view - do not change anything
        if (data === undefined || !data.isMainNavigation) return;

        if (data.hasPreorderTimeStep) {
            isHomeButtonVisible.value = false;
            isGoBackButtonVisible.value = true;
        } else {
            isGoBackButtonVisible.value = false;
            isHomeButtonVisible.value = true;
        }
    }

    function onBecomeInvisible() {
        isViewVisible.value = false;
        TastierClient.context.onCartChanged.remove(updateCart);
    }

    function redrawList() {
        offerOverviewComponent.value?.redrawList();
        updateCart();
    }

    function reset() {
        TastierClient.context.onCartChanged.remove(updateCart);
        needsOfferReload = true;

        if (isViewVisible.value) {
            onBecomeVisible();
        }
    }

    function updateCart() {
        const offer = TastierClient.context.currentOffer;
        let articles = 0;
        cart.setCurrentCart(offer?.cart ?? null);

        if (offer?.cart) {
            totalAmount.value = toMoneyWithSign(offer.cart.totalAfterDiscountsAndVouchers);
            if (offer.cart.items.length > 0 && offer.cart.totalAfterDiscountsAndVouchers >= 0) {
                isPayButtonEnabled.value = true;
            } else {
                isPayButtonEnabled.value = false;
            }

            for (const cartItem of offer.cart.items) {
                articles += cartItem.quantity;
            }
        } else {
            totalAmount.value = toMoneyWithSign(0);
            isPayButtonEnabled.value = false;
        }

        selectedArticles.value = articles;

        offerOverviewComponent.value?.updateCart();
    }

    function onArticleClick(article: ICustomerArticle) {
        if (addToCardPopover === undefined) {
            addToCardPopover = new AddToCardPopover();
        }

        addToCardPopover.forArticle(article).show();
    }

    async function quickAddItem(item: ICustomerArticleWithOptions) {
        try {
            const { selectedOptions, isAddToCartAvailable, addItemToCart, fixCurrentQuantity } = useAddToCart(
                item,
                offer.orderService,
                true
            );
            fixCurrentQuantity();
            selectedOptions.value = getPreselectedOptions(item);

            if (isAddToCartAvailable.value) {
                await addItemToCart();
                // onCartItemAdded();
            } else {
                throw new Error("Add to cart is not available");
            }
        } catch (ex) {
            logError(ex);
            Popup.show({ message: trans.cart.failedToAdd });
        }
    }

    function onScrolledChanged(isScrolled: boolean) {
        if (isScrolled) {
            OrderController.onScrolled.dispatch();
        } else {
            OrderController.onNotScrolled.dispatch();
        }
    }

    function displayConfirmationPopover() {
        const fulfillmentBtn = document.querySelector(".fulfillmentbtn") as HTMLButtonElement | null;
        const returnFromCart = document.querySelector(".view.cart.animright") as HTMLElement | null;
        const orderOverviewPage = document.querySelector(".view.order.shown") as HTMLElement | null;
        const returnOrderHistory = document.querySelector(".view.orderhistory.animright") as HTMLElement | null;
        const returnOrderHistoryOnDone = document.querySelector(
            ".view.order.fadetransition.shown"
        ) as HTMLElement | null;

        if (
            fulfillmentBtn &&
            orderOverviewPage &&
            !returnFromCart &&
            (!returnOrderHistory || returnOrderHistoryOnDone)
        ) {
            fulfillmentBtn.click();
        }
    }

    function onGoHomeButtonClick() {
        OrderController.onShowServices.dispatch();
    }

    function onGoBackButtonClick() {
        OrderController.onGoBack.dispatch();
    }

    function onProceedButtonClick() {
        OrderController.onContinue.dispatch();
    }

    function updateTitle() {
        const serviceDesignation = Helper.getCurrentServiceDesignation();
        title.value = serviceDesignation ?? trans.order.title;
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .order {
        > .head {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            transition: top 0.6s variables.$easeOutExpo;
            z-index: 500;
        }

        > .index {
            height: 78px;
            position: absolute;
            top: 53px;
            left: 0;
            right: 0;
            z-index: 210;
            background-color: #fff;
            transition:
                height 0.6s variables.$easeOutExpo,
                top 0.6s variables.$easeOutExpo,
                background-color 0.2s linear;

            > .categoryslider {
                transition: height 0.6s variables.$easeOutExpo;

                > .slidable {
                    transition:
                        height 0.6s variables.$easeOutExpo,
                        padding 0.2s linear;
                }
            }
        }

        > .itemlist {
            position: absolute;
            top: 0;
            bottom: 104px;
            left: 0;
            right: 0;
            transition: top 0.6s variables.$easeOutExpo;
            z-index: 200;
            padding-top: 131px;

            > .section {
                &:last-child {
                    padding-bottom: 32px;
                }

                > .header {
                    position: relative;

                    &.nameonly {
                        > .name {
                            border-top: 1px solid #ededed;
                            border-bottom: 1px solid #ededed;
                            background-color: #f5f5f5;
                            min-height: 54px;
                            padding: 22px 22px 6px 22px;
                            box-sizing: border-box;

                            &.twolines {
                                padding-top: 8px;
                            }

                            > h2 {
                                margin: 0;
                                font-size: 16px;
                                line-height: 20px;
                                font-weight: 400;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                color: colors.$fontColor;
                            }

                            > .description {
                                font-size: 12px;
                                line-height: 16px;
                                color: colors.$lightGrey;
                            }
                        }
                    }

                    &.withimage {
                        height: 130px;

                        > .background {
                            position: absolute;
                            left: 0;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            background-position: center center;
                            background-repeat: no-repeat;
                            background-size: cover;
                            z-index: 21;
                        }

                        > .blackdim {
                            position: absolute;
                            left: 0;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            z-index: 22;
                            background-color: rgba(0, 0, 0, 0.5);
                        }

                        > .name {
                            position: relative;
                            z-index: 25;
                            display: flex;
                            height: 100%;
                            width: 100%;
                            flex-direction: column;
                            color: #fff;
                            text-align: center;
                            justify-content: center;
                            align-items: center;

                            > h2 {
                                margin: 0;
                                font-size: 18px;
                                font-weight: 400;
                                letter-spacing: 0.2px;
                                padding: 6px 0 6px 0;
                            }

                            > .description {
                                font-size: 12px;
                                font-weight: 300;
                            }
                        }
                    }
                }

                > .articles {
                    > .article {
                        position: relative;
                        padding: 0 22px 0 22px;

                        &.added > .name {
                            > .qty {
                                opacity: 1;
                                transform: translate3d(0, 0, 0);
                            }

                            > h3 {
                                padding-left: 28px;
                            }
                        }

                        > .name {
                            position: relative;
                            padding: 14px 68px 14px 0;

                            > .qty {
                                position: absolute;
                                left: 0;
                                top: 14px;
                                color: var(--colorPrimary);
                                font-size: 16px;
                                font-weight: 600;
                                line-height: 18px;
                                display: block;
                                height: 18px;
                                width: 28px;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                opacity: 0;
                                transform: translate3d(-28px, 0, 0);
                                transition:
                                    transform 0.6s variables.$easeOutExpo,
                                    opacity 0.6s variables.$easeOutExpo;
                            }

                            > h3 {
                                color: colors.$fontColor;
                                font-size: 16px;
                                font-weight: 300;
                                margin: 0;
                                line-height: 18px;
                                transition: padding 0.6s variables.$easeOutExpo;
                            }

                            > .description {
                                color: colors.$lightGrey;
                                font-size: 12px;
                                line-height: 16px;
                            }
                        }

                        > .price {
                            position: absolute;
                            right: 26px;
                            top: 13px;
                        }

                        > .line {
                            height: 1px;
                            position: absolute;
                            background-color: #f5f5f5;
                            left: 23px;
                            right: 23px;
                            bottom: 0;
                        }
                    }

                    &.withimage {
                        > .article {
                            &.hasimage {
                                min-height: 84px;
                            }

                            > .name {
                                border: none;
                                padding-top: 11px;
                                padding-bottom: 8px;
                                padding-right: 88px;

                                > .qty {
                                    top: 11px;
                                }
                            }

                            > .price {
                                position: relative;
                                top: unset;
                                right: unset;
                                padding-bottom: 8px;
                            }

                            > .image {
                                position: absolute;
                                top: 50%;
                                margin-top: -37px;
                                right: 23px;
                                width: 74px;
                                height: 74px;
                                border-radius: 4px;
                                overflow: hidden;

                                > img {
                                    height: 100%;
                                    width: 100%;
                                    object-fit: contain;
                                    object-position: center;
                                }
                            }
                        }
                    }
                }
            }

            > .loading {
                position: absolute;
                left: 50%;
                white-space: nowrap;
                transform: translateX(-50%);

                > .loader {
                    background-image: url("/assets/img/app/loading_green.svg");
                }
            }

            > .nodata {
                padding: 0 20px 0 20px;

                > .lbl {
                    text-align: center;
                    font-size: 14px;
                }
            }
        }

        > .scrolltop {
            position: absolute;
            bottom: 50px;
            right: 15px;
            background-color: var(--colorPrimary);
            z-index: 201;
            height: 44px;
            width: 44px;
            border-radius: 50%;
            text-align: center;
            cursor: pointer;
            -webkit-box-shadow: 0px 0px 7px 0px rgba(72, 72, 72, 0.11);
            -moz-box-shadow: 0px 0px 7px 0px rgba(72, 72, 72, 0.11);
            box-shadow: 0px 0px 7px 0px rgba(72, 72, 72, 0.11);
            transition: bottom 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

            @include mixins.hover-and-active() {
                background-color: var(--colorPrimaryHover);
            }

            > .fal {
                font-size: 18px;
                color: #fff;
                line-height: 44px;
            }
        }

        &.scrolled {
            > .head {
                top: -53px;
            }

            > .index {
                top: 0;
                height: 52px;
                background-color: #f5f5f5;
                border-bottom: 1px solid #dcdcdc;

                > .categoryslider {
                    height: 52px;

                    > .slidable {
                        height: 52px;
                        padding-top: 6px;
                        padding-bottom: 6px;

                        > .category:not(.active) {
                            background-color: transparent;
                        }
                    }
                }
            }

            > .scrolltop {
                bottom: 117px;
            }
        }

        > .bottom {
            height: 104px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 220;
            padding: 0 12px;
            background-color: #fff;
            -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);

            > .selectionsum {
                text-align: center;
                font-size: 14px;
                color: colors.$fontColor;
                height: 38px;
                line-height: 38px;
                letter-spacing: 0.3px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
</style>
